import { z } from 'zod';

/**
 * Los datos de **Copathy misma** — la organización que usa la plataforma.
 *
 * ── Por qué está separado de `empresas` aunque viva en la misma tabla ──
 * Copathy es una fila de `empresas` con `tipo = 'proveedor'`, y **todas** las
 * consultas del módulo de clientes llevan `tipo = 'cliente'` para que nunca se
 * mezclen. Eso es correcto y hay que conservarlo: el día que Copathy apareciera
 * en el listado de clientes, alguien le rotaría el código de registro.
 *
 * Por eso su ficha tiene su propio contrato y su propia ruta, y no un caso
 * especial dentro del módulo de clientes.
 */

export const esquemaOrganizacion = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  rif: z.string().nullable(),
  descripcion: z.string().nullable(),
  pais: z.string().nullable(),
  direccion: z.string().nullable(),
  correo: z.string().nullable(),
  telefono: z.string().nullable(),
  sitioWeb: z.string().nullable(),
  instagram: z.string().nullable(),
  /** Relato de marca. Hoy no lo muestra ninguna pantalla del trabajador. */
  mision: z.string().nullable(),
  vision: z.string().nullable(),
  valores: z.string().nullable(),
  historia: z.string().nullable(),
  tieneLogo: z.boolean(),
});
export type Organizacion = z.infer<typeof esquemaOrganizacion>;

/**
 * ⚠️ El **nombre no se puede vaciar** y lo demás sí.
 *
 * `.nullable()` en los opcionales no es adorno: mandar `null` es cómo se borra un
 * campo. Sin eso, un dato cargado por error —un Instagram equivocado— no habría
 * forma de quitarlo, solo de sustituirlo por otra cosa.
 */
export const esquemaEditarOrganizacion = z
  .object({
    nombre: z.string().trim().min(2, 'El nombre es demasiado corto.').max(160).optional(),
    rif: z.string().trim().max(40).nullable().optional(),
    descripcion: z.string().trim().max(1000).nullable().optional(),
    pais: z
      .string()
      .trim()
      .length(2, 'El país va como código de dos letras.')
      .toLowerCase()
      .nullable()
      .optional(),
    direccion: z.string().trim().max(300).nullable().optional(),
    correo: z
      .string()
      .trim()
      .email('Ese correo no se ve bien.')
      .max(160)
      .nullable()
      .optional()
      // Una cadena vacía no es un correo inválido: es «quítalo».
      .or(z.literal('').transform(() => null)),
    telefono: z.string().trim().max(40).nullable().optional(),
    sitioWeb: z
      .string()
      .trim()
      .max(200)
      .nullable()
      .optional()
      .refine(
        (v) => !v || /^https?:\/\//i.test(v),
        'La dirección web tiene que empezar por http:// o https://',
      ),
    instagram: z.string().trim().max(80).nullable().optional(),
    mision: z.string().trim().max(2000).nullable().optional(),
    vision: z.string().trim().max(2000).nullable().optional(),
    valores: z.string().trim().max(4000).nullable().optional(),
    historia: z.string().trim().max(8000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'No enviaste ningún cambio.');
export type EntradaEditarOrganizacion = z.infer<typeof esquemaEditarOrganizacion>;
